import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import {
  FormSelectChoice,
  FormFieldComponentProps,
  VerificationServiceProps,
  Organization,
  ActiveMilitaryPersonalInfoViewModel,
  Country,
} from "../../../lib/types/types";
import {
  getFieldConfigByFieldId,
  orgToOption,
} from "../../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { FormFieldInputSelect } from "../FormFieldInputSelect/FormFieldInputSelect";
import { fetchOrganization } from "../../../lib/ServerApi/OrganizationApiClient";
import { logger } from "../../../lib/utils/logger/logger";
import { getSafe } from "../../../lib/utils/getSafe";
import { isCountrySelectionNeeded } from "../Country/countryUtils";

interface BranchOfServiceComponentProps {
  /**
   * @deprecated Orgs are fetch lazily, within this component, now
   * A list of orgs is no-longer passed-in and then filtered
   * Don't provide this any more
   */
  organizations?: Organization[];
}

export const BranchOfService = ({
  autoFocus,
  onChange,
  value,
  isErrored,
  organizations = undefined,
  verificationService,
  intl,
  placeholder = "",
  disabled = false,
}: FormFieldComponentProps & VerificationServiceProps & BranchOfServiceComponentProps) => {
  if (organizations) {
    logger.warn("Param `organizations` is deprecated and will not be used");
  }
  const inputId = "branch-of-service";
  const [options, setOptions] = React.useState<FormSelectChoice[]>([]);
  const [disabledState, setDisabledState] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { viewModel, programTheme } = verificationService;
  const { status } = viewModel as ActiveMilitaryPersonalInfoViewModel;
  const { orgSearchUrl } = programTheme.config;
  const countryChoiceValue: Country = getSafe(() => viewModel?.countryChoice?.value);
  const needToPickCountry = isCountrySelectionNeeded(programTheme, countryChoiceValue);

  React.useEffect(() => {
    const controller = new AbortController();

    const fetchOrgs = async () => {
      if (needToPickCountry) {
        setOptions([]);
        setDisabledState(true);
        return;
      }
      setDisabledState(false);
      setIsLoading(true);
      const fetchUrl = new URL(orgSearchUrl);
      if (countryChoiceValue) {
        fetchUrl.searchParams.set("country", countryChoiceValue);
      } else {
        fetchUrl.searchParams.delete("country");
      }

      if (status) {
        fetchUrl.searchParams.set("tags", status);
      } else {
        fetchUrl.searchParams.delete("tags");
      }

      const orgSearchResponse = await fetchOrganization(fetchUrl.toString());
      if (controller.signal.aborted) return;
      if (orgSearchResponse) setOptions(orgSearchResponse.map(orgToOption));
      setIsLoading(false);
    };

    fetchOrgs();

    return () => {
      controller.abort();
    };
  }, [countryChoiceValue, status]);

  const handleStateChange = (selectedOrgOption: FormSelectChoice, onChange: Function) => {
    if (selectedOrgOption) {
      onChange({
        id: selectedOrgOption.value,
        name: selectedOrgOption.label,
      });
    } else {
      onChange({
        id: 0,
        name: "",
      });
    }
  };

  const isFieldHidden = getFieldConfigByFieldId("organization")?.hidden;

  if (isFieldHidden) {
    return null;
  }

  return (
    <FormFieldInputSelect
      fieldId={inputId}
      refId="organization"
      autoFocus={autoFocus}
      options={options}
      onChange={(branch: FormSelectChoice) => handleStateChange(branch, onChange)}
      value={options.find((option) => option.value === (value && value.value))}
      placeholder={
        placeholder ||
        intl.formatHTMLMessage({
          id: "branchOfServicePlaceholder",
          defaultMessage: "Branch Of Service",
        })
      }
      isRequired
      isLoading={isLoading}
      disabled={disabledState || disabled}
      explanation={
        disabledState ? (
          <FormattedHTMLMessage id="chooseCountry" defaultMessage="Choose a country first" />
        ) : null
      }
      label={<FormattedHTMLMessage id="branchOfService" defaultMessage="Branch Of Service" />}
      errorMessage={
        isErrored && (
          <FormattedHTMLMessage
            id="errorId.invalidOrganization"
            defaultMessage="You must select an organization from the list"
          />
        )
      }
      showPlaceholderAndHideLabel={!!placeholder}
      containerId={`sid-${inputId}-id`}
    />
  );
};

export const BranchOfServiceComponent = injectIntl(BranchOfService);
